import { AxiosError } from 'axios';

import { CAST_AI } from '@cast/constants';
import {
  Tab,
  TabContent,
  Tabs,
  TabsProvider,
  Skeleton,
  Card,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';
import { Cluster } from '@cast/types';
import { CloudVolume } from '@cast/types-stop';

import { NodeWithDependencies } from 'features/cluster/nodes/types';

import { ManagedDiskAllTabContent } from './all-tab';
import { ManagedDiskTabContent } from './managed-disk-tab';

import { FailedToLoad } from '@cast/shared-messages-ui';

const ALL_TAB = 'all';

type ManagedDiskSectionProps = {
  node?: NodeWithDependencies;
  cluster: Cluster;
  disks: CloudVolume[];
  isLoading: boolean;
  error: AxiosError | null;
  refetch: () => void;
  isCloudDisksEnabled?: boolean;
};

export const ManagedDiskSection = ({
  node,
  cluster,
  disks,
  isLoading,
  error,
  refetch,
  isCloudDisksEnabled,
}: ManagedDiskSectionProps) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={16}
      data-testid="managed-disk-section"
    >
      <Stack direction="column" gap={4}>
        <Box display="flex" alignItems="center" gap={4}>
          <Typography variant="h6">Managed disks</Typography>
        </Box>

        <Typography variant="P12R" color="textSecondary">
          {CAST_AI} managed disks are added based on usage to dynamically handle
          ephemeral storage needs.
        </Typography>
      </Stack>

      {isLoading ? (
        <Skeleton variant="rectangular" height={400} />
      ) : error ? (
        <Card
          sx={{
            height: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FailedToLoad title={error.message} refresh={refetch} />
        </Card>
      ) : (
        <TabsProvider initialTab={ALL_TAB}>
          <Tabs variant="scrollable" scrollButtons="auto">
            <Tab
              value={ALL_TAB}
              label={`All ${!error && `(${disks.length})`}`}
            />
            {disks.map((disk) => (
              <Tab
                key={disk.volumeId}
                value={disk.volumeId}
                label={disk.volumeId}
              />
            ))}
          </Tabs>

          <TabContent value={ALL_TAB}>
            <ManagedDiskAllTabContent
              node={node}
              cluster={cluster}
              isCloudDisksEnabled={isCloudDisksEnabled}
            />
          </TabContent>
          {disks.map((disk) => (
            <TabContent key={disk.volumeId} value={disk.volumeId || ''}>
              <ManagedDiskTabContent
                node={node}
                cluster={cluster}
                disk={disk}
                isCloudDisksEnabled={isCloudDisksEnabled}
              />
            </TabContent>
          ))}
        </TabsProvider>
      )}
    </Box>
  );
};
